import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * Announcement banner custom element that allows fading between content.
 *
 * Stacked (slider) on mobile always. On desktop, stacks only when display mode is slider.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    }

    this.current = 0;

    if (this.isStacked) {
      this.play();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.suspend();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);

    if (window.Shopify?.designMode) {
      document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    }
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || !this.isStacked || this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get displayMode() {
    return this.dataset.displayMode || 'slider';
  }

  /**
   * Whether announcements should stack as a slider.
   * Always true on mobile; on desktop, only when display mode is slider.
   */
  get isStacked() {
    return this.displayMode === 'slider' || !mediaQueryLarge.matches;
  }

  /**
   * Slide items: top-level children except divider blocks.
   * @returns {HTMLElement[]}
   */
  get slides() {
    const container = this.querySelector('.announcement-bar__slides');

    if (!container) return [];

    const results = [];

    for (const child of container.children) {
      if (child instanceof HTMLElement && !child.classList.contains('divider')) {
        results.push(child);
      }
    }

    return results;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.slides;

    if (slides.length === 0) return;

    this.#current = current;

    if (!this.isStacked) {
      this.#revealAllSlides();
      return;
    }

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    for (const [index, slide] of slides.entries()) {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    }
  }

  #revealAllSlides() {
    for (const slide of this.slides) {
      slide.setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  /**
   * Switch between stacked slider and all-visible layouts when the viewport changes.
   */
  #handleBreakpointChange = () => {
    this.suspend();

    if (this.isStacked) {
      this.paused = false;
      this.current = this.#current;
      this.play();
      return;
    }

    this.#revealAllSlides();
  };

  /**
   * Show the selected announcement when a block is selected in the theme editor.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLElement) || !this.contains(target)) return;

    const slide = this.slides.find((item) => item === target || item.contains(target));

    if (!slide) return;

    this.current = this.slides.indexOf(slide);
    this.pause();
  };

  /**
   * Resume autoplay when a block is deselected in the theme editor.
   * @param {Event} event
   */
  #handleBlockDeselect = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLElement) || !this.contains(target)) return;

    this.paused = false;
    this.play();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
